import {
  ShopApi,
} from '../constants/shop-api';
import {
  getCart,
  getCartContext,
  getGuestCartToken,
  getMagentoCartQuoteId,
} from '../../features/cart/shop-cart/store/shop-cart.selectors';
import { ROLES } from '../../features/user-profile';
import { selectUser } from '../../features/user-profile/user-profile.selectors';
import {
  AddProductMagentoRequest,
  AddProductRequest,
  AddToCartUrlContextParam,
  MagentoProductIdentification,
  Product,
  ProductDetails,
  ProductIdentification,
  ShopCart,
} from '../../features/cart/shop-cart/interfaces/shop-cart.interface';
import { StoreState } from '../../store/store.interface';
import { UserProfile } from '../../features/user-profile/user-profile.interface';
import store from '../../store/store';
import { addDays } from './date-utils';
import { isHeStorePurchaseFlow, isK12StorePurchaseFlow, isTunnelPurchaseFlow, PROFESSIONAL } from './util';
import environment, { CheckoutFlow, ENV, isGuestCheckoutPage } from '../environment';
import { isCustomerLoginSuccess } from '../../features/customer/customer';
import getCookie, { COOKIE_CART, COOKIE_CART_DOMAIN, COOKIE_CART_PATH } from '../cookies';
import { isProductOutOfStock } from '../../features/cart/shop-cart/mapping/shop-cart.mapping';
import { PaymentOrderItemsProductMetaData } from '../../features/order-confirmation/order-completion.interface';
import { ItemType } from '../../features/product-card/product.interface';
import { BusinessUnit } from '../../features/checkout/shop-checkout.interface';
import { buildSkuFromIsbn, buildSkuFromOfferPricingId, getPrimaryIsbn } from './product-util';
import { getCartPageLocalStorageObject } from '../../features/search/utils/search.util';
import { getSharpenTunnelFlowProperties } from './analytics-util';
import { getCartAnalyticsOverrides } from '../../features/cart/shop-cart/shop-cart.analytics';

export const NATIONAL_STORE = 'national';
export const K12_OUT_OF_STOCK_ERROR_CODE = 'k12.out.of.stock';
export const PRODUCT_PK12_ONLY_ERROR_CODE = 'product.pk12only';
export const STANDALONE_APPLICATION = 'standalone';
export const PRODUCT_AVAILABLE = 1;
export const CART_INVALID = 0;

const env = environment();

export enum CartFormatCode {
  BB = 'BB',
  BC = 'BC',
  BD = 'BD',
  BE = 'BE',
  DH = 'DH',
  DG = 'DG',
  GO = 'GO',
  eBook = 'eBook',
}

const CartFormatText: { [key: string]: string } = {
  hardback: 'Hardback / Paperback',
  looseleaf: 'Looseleaf',
  spiral: 'Spiral Bound',
  digitalAccess: 'Digital Access',
  accessCard: 'Access Card',
  ebook: 'eBook',
  go: 'McGraw Hill GO',
};

export declare interface ShopCartUrlContext {
  isLoggedIn: boolean;
  storeCode?: string;
  shareCartId?: string;
  guestCartId?: string;
  zipCode?: string;
  promoCode?: string;
  addProductRequest?: AddProductRequest;
  addProductMagentoRequest?: AddProductMagentoRequest;
  storeType?: string;
  forceNewProduct?: boolean;
  magentoCheckoutFlow?: CheckoutFlow;
}

export function getShopShareCartUrlMagento(state: StoreState): string {
  if (isCustomerLoginSuccess(state)) {
    return ShopApi.getUserShareCartMagento;
  } else {
    const guestCartToken = getGuestCartToken(state);
    return ShopApi.getGuestShareCartMagento.replace(':cartToken', guestCartToken);
  }
}

export function getShopShareCartMergeUrlMagento(state: StoreState): string {
  if (isCustomerLoginSuccess(state)) {
    return ShopApi.getShopUserShareCartMergeMagento;
  } else {
    const guestCartToken = getGuestCartToken(state);
    return ShopApi.getShopGuestShareCartMergeMagento.replace(':cartToken', guestCartToken);
  }
}

/**
 * Check login condition, if not return login url
 */
export function getCheckoutUrl(state: StoreState, store?: string): string {
  if (selectUser(state) && isHEInstructor(selectUser(state))) {
    if (env.env === ENV.local) {
      return `${window.location.origin}/content/mheducation/us/en/checkout/sample.html?wcmmode=disabled`;
    } else {
      return `${env.mheducationUrl}/checkout/sample.html?wcmmode=disabled`;
    }
  } else {
    const shopCartUrlContext = getCartContext(state, store);
    return shopCartUrlContext?.isLoggedIn
      ? getCheckoutPageUrl(shopCartUrlContext.storeCode)
      : getLoginUrl(state, store);
  }
}

export function bulkAddToCartUrlMagento(state: StoreState, addItemsToMineCart: boolean): string {
  if (isCustomerLoginSuccess(state)) {
    if (addItemsToMineCart) {
      return ShopApi.getBulkItemsAddToCart;
    }
    const cartId = getMagentoCartQuoteId(state);
    return ShopApi.getBulkUserAddToCart.replace(':quoteId', cartId);
  } else {
    const guestCartToken = getGuestCartToken(state);
    return ShopApi.getBulkGuestAddToCart.replace(':cartToken', guestCartToken);
  }
}


export function removeFromOldCartCookie(isbn: string) {
  const cartCookie = getCookie(COOKIE_CART);
  if (cartCookie) {
    const cartCookieObj = JSON.parse(cartCookie.substring(1, cartCookie.length - 1).replace(/\\/g, ''));
    if (cartCookieObj[isbn]) {
      delete cartCookieObj[isbn];
      const newCookie = String.raw`${JSON.stringify(cartCookieObj).replace(/"/g, '\\"')}`;
      document.cookie = `${COOKIE_CART}="${newCookie}";domain=${COOKIE_CART_DOMAIN};path=${COOKIE_CART_PATH}`;
    }
  }
}

export function addToCartUrlContext(
  shopCartUrlContext: ShopCartUrlContext,
  { businessUnit, isbn, isbn13, quantity, freeTrial, offerPricingId, instructorSampling }: AddToCartUrlContextParam
): ShopCartUrlContext {
  // make sure that all the keys are in uppercase as its read like this - catalogMapping[businessUnit?.toUpperCase()]
  const catalogMapping: { [key: string]: string } = {
    HE: 'HE',
    K12: 'SEG',
    SEG: 'SEG',
    'SEG-STORE': 'SEG',
    'SEG-PRODUCT': 'SEG',
    'SEG-PROGRAM': 'SEG',
    PROFESSIONAL: PROFESSIONAL,
  };
  const productIdentifier: ProductIdentification = {
    mhid: isbn.length != 13 ? isbn : undefined,
    isbn10: isbn.length == 10 ? isbn : undefined,
    isbn13: isbn13 || (isbn.length == 13 ? isbn : undefined),
    offerPricingId: offerPricingId,
    businessUnit: catalogMapping[businessUnit?.toUpperCase()],
    context: undefined,
    catalog: undefined,
    productType: undefined,
  };

  // [SHOP-API-REMOVAL][WECOMM-20796] Shop add-to-cart paths (HE Instructor, SEG national cart)
  // are removed. If these flows are encountered in prod, log so we can locate the call site.
  if (isHEInstructor(selectUser(store.getState() as StoreState))) {
    console.warn(
      '[SHOP-API-REMOVAL][WECOMM-20796] addToCartUrlContext: HE Instructor flow routed to Magento (Shop addProductRequest removed). isbn=%s businessUnit=%s',
      isbn,
      productIdentifier.businessUnit
    );
  }
  if (productIdentifier.businessUnit === 'SEG') {
    console.warn(
      '[SHOP-API-REMOVAL][WECOMM-20796] addToCartUrlContext: SEG add-to-cart routed to Magento (Shop /store/cart/addproduct removed). isbn=%s',
      isbn
    );
  }

  /*
    If offerPricingId is present, it means the offer is from offer pricing
  */
  const useEdoms = !!offerPricingId;
  let sku: string;

  if (useEdoms) {
    sku = buildSkuFromOfferPricingId(offerPricingId, productIdentifier.businessUnit);
  } else {
    sku = buildSkuFromIsbn(
      getPrimaryIsbn(productIdentifier.isbn13, productIdentifier.isbn10, productIdentifier.mhid),
      productIdentifier.businessUnit
    );
  }

  const extensionAttributes = {
    ...(freeTrial ? { free_trial: true } : {}),
    ...(useEdoms ? { product_offer_id: offerPricingId } : {}),
    ...(instructorSampling ? { instructor_sampling: true } : {}),
  };

  const magentoProductIdentifier: MagentoProductIdentification = {
    sku: sku,
    qty: quantity,
    ...(Object.keys(extensionAttributes).length ? { extension_attributes: extensionAttributes } : {}),
  };

  shopCartUrlContext.addProductMagentoRequest = {
    cartItem: magentoProductIdentifier,
  };
  return shopCartUrlContext;
}

export function isHEInstructor(user: UserProfile) {
  return isInstructor(user) && user.persona?.segment.toLowerCase() === 'he';
}

export function isInstructor(user: UserProfile) {
  return user?.userType == ROLES.INS;
}

export function isHomeschooler(cart?: ShopCart) {
  return cart?.userRole?.toLowerCase() === 'homeschooler' && cart?.userBusinessUnit?.toLowerCase() === 'seg';
}

export function isK12Cart(cart?: ShopCart) {
  return cart?.orderBusinessUnit?.toLowerCase() === 'seg';
}

export function isK12Admin(cart?: ShopCart) {
  return cart?.userRole?.toLowerCase() === 'admin' && cart?.userBusinessUnit?.toLowerCase() === 'seg';
}

export function getCartPageUrl(user: UserProfile | undefined, store = NATIONAL_STORE): string {
  if (user && isHEInstructor(user)) {
    if (env.env === ENV.local) {
      return `${window.location.origin}/content/mheducation/us/en/checkout/sample.html?wcmmode=disabled`;
    } else {
      return `${env.mheducationUrl}/checkout/sample.html`;
    }
  } else if (env.env === ENV.local) {
    if (isCustomStore(store) && store !== CheckoutFlow.hestores) {
      return `${window.location.origin}/content/mheducation/us/en/checkout/cartk12storefront.store-${store}.html?wcmmode=disabled`;
    } else {
      return `${window.location.origin}/content/mheducation/us/en/checkout/${
        isTunnelPurchaseFlow()
          ? `tunnel-confirm.html${location.search}`
          : `${store === CheckoutFlow.hestores ? 'hestore-' : ''}cart.html?wcmmode=disabled`
      }`;
    }
  } else {
    return isTunnelPurchaseFlow()
      ? `${env.mheducationUrl}/checkout/tunnel-confirm${location.search}`
      : store === CheckoutFlow.hestores
        ? `${env.mheducationUrl}/checkout/highered-store/cart`
        : `${env.mheducationUrl}/checkout${getStorePath(store)}/cart`;
  }
}

export function getAuthoredStorePrePath() {
  if (isTunnelPurchaseFlow()) {
    return 'tunnel-';
  } else if (isHeStorePurchaseFlow()) {
    return 'hestore-';
  } else {
    return '';
  }
}

export function getAuthoredStorePostPath() {
  if (isK12StorePurchaseFlow()) {
    return 'k12storefront';
  } else {
    return '';
  }
}

export function getStorePath(store: string) {
  if (isK12StorePurchaseFlow()) {
    return '/' + store;
  } else if (isHeStorePurchaseFlow()) {
    return '/highered-store';
  } else {
    return '';
  }
}

export function getContinueShoppingUrl(
  storeCode = NATIONAL_STORE,
  isHeCustomCart: boolean,
  storeCatalog?: string
): string {
  if (isCustomStore(storeCode)) {
    return `${env.mheducationUrl}/prek-12/custom/store/${storeCode}.html`;
  } else if (isHeCustomCart) {
    return `${env.mheducationUrl}/highered/bookstore.html`;
  } else if (storeCatalog) {
    return `${env.mheducationUrl}/highered/custom/store/${storeCatalog}.html`;
  }
  return `${env.mheducationUrl}`;
}

export function getSuccessUrl(store = NATIONAL_STORE): string {
  const tunnelPurchaseSuccessPath = isTunnelPurchaseFlow() ? 'tunnel-' : '';

  if (env.env === ENV.local) {
    if (isGuestCheckoutPage()) {
      if (environment().checkoutFlow === CheckoutFlow.tunnelPurchase) {
        return `${window.location.origin}/content/mheducation/us/en/checkout/guest/tunnel-payment/success.html?wcmmode=disabled`;
      } else {
        return `${window.location.origin}/content/mheducation/us/en/checkout/guest/national/success.html?wcmmode=disabled`;
      }
    }
    return `${
      window.location.origin
    }/content/mheducation/us/en/checkout/${getAuthoredStorePrePath()}success${getAuthoredStorePostPath()}.html?wcmmode=disabled`;
  } else {
    if (isGuestCheckoutPage()) {
      if (environment().checkoutFlow === CheckoutFlow.tunnelPurchase) {
        return `${env.mheducationUrl}/checkout/tunnel-success/guest.html`;
      } else {
        return `${env.mheducationUrl}/checkout/success/guest.html`;
      }
    }
    return `${env.mheducationUrl}/checkout${getStorePath(store)}/${tunnelPurchaseSuccessPath}success.html`;
  }
}

export function getBookSelfUrl(): string {
  return env.myBookShelfUrl;
}

export function getAccountUrl(): string {
  return env.accountUrl;
}

function getCheckoutPageUrl(store = NATIONAL_STORE) {
  if (env.env === ENV.local) {
    return `${
      window.location.origin
    }/content/mheducation/us/en/checkout/${getAuthoredStorePrePath()}payment${getAuthoredStorePostPath()}.html?wcmmode=disabled`;
  } else if (isTunnelPurchaseFlow()) {
    return `${env.mheducationUrl}/checkout${getStorePath(store)}/tunnel-payment${window.location.search}`;
  } else {
    return `${env.mheducationUrl}/checkout${getStorePath(store)}/payment`;
  }
}

export function getLoginUrl(state: StoreState, store: string | undefined) {
  const shopCartUrlContext = getCartContext(state, store);
  const user = selectUser(state);
  const loginUrl = new URL(env.shopUrl + ShopApi.loginUrl);
  const guestCartId = getGuestCartToken(state);
  loginUrl.searchParams.set('magentoCart', env.checkoutFlow);
  if (guestCartId) {
    loginUrl.searchParams.set('guestcartid', guestCartId);
  }
  if (isCustomStoreGuest(shopCartUrlContext.isLoggedIn, shopCartUrlContext.storeCode) && shopCartUrlContext.storeCode) {
    loginUrl.searchParams.set('storeCode', shopCartUrlContext.storeCode);
    loginUrl.searchParams.set('redirectUrl', getCartPageUrl(user, shopCartUrlContext.storeCode));
    loginUrl.searchParams.set('storeType', shopCartUrlContext.storeType!);
  }
  if (env.checkoutFlow === CheckoutFlow.hestores) {
    loginUrl.searchParams.set('redirectUrl', `${env.mheducationUrl}/checkout/highered-store/cart`);
  }
  return loginUrl.toString();
}

export function getHEsampleExitUrl() {
  return `${env.shopUrl}`;
}

export function getCartItemFormat(format: string = '', formatCode: string, isVirtual: boolean) {
  switch (format) {
    case CartFormatCode.eBook:
      return format;
    default:
      format = toCapCamelCase(format);
  }
  switch (formatCode) {
    case CartFormatCode.BB:
      format = CartFormatText.hardback;
      break;
    case CartFormatCode.BC:
      format = CartFormatText.hardback;
      break;
    case CartFormatCode.BD:
      format = CartFormatText.looseleaf;
      break;
    case CartFormatCode.BE:
      format = CartFormatText.spiral;
      break;
    case CartFormatCode.DG:
      format = CartFormatText.ebook;
      break;
    case CartFormatCode.DH:
      format = isVirtual ? CartFormatText.digitalAccess : CartFormatText.accessCard;
      break;
    case CartFormatCode.GO:
      format = CartFormatText.go;
      break;
    default:
      format = toCapCamelCase(format);
  }
  return format.toLowerCase() === 'mcgraw hill go' ? 'McGraw Hill GO' : format;
}

function isCustomStoreGuest(isLoggedIn: boolean, storeCode: string | undefined): boolean {
  return !isLoggedIn && isCustomStore(storeCode);
}

export function isCustomStore(storeCode: string | undefined): boolean {
  return !!storeCode && storeCode != NATIONAL_STORE;
}

/**
 * @param name ex : CONNECT WITH LEARNSMART
 * @returns Connect With Learnsmart
 */
export function toCapCamelCase(name: string): string {
  return name ? name.toLowerCase().replace(/\b(\w)/g, (capture) => capture.toUpperCase()) : name;
}

export function getSampleUrl() {
  if (env.env === ENV.local) {
    return `${window.location.origin}/content/mheducation/us/en/checkout/sample.html?wcmmode=disabled`;
  } else {
    return `${env.mheducationUrl}/checkout/sample.html?wcmmode=disabled`;
  }
}

export function getProductEndAccessDate(term: string): string {
  if (!term) {
    return '';
  }

  if (term.toLowerCase() === 'always' || term === '-1') {
    return 'Unlimited';
  }
  const duration = (term || '').match(/\d+/);
  return duration ? addDays(new Date(), Number(duration![0])).toLocaleDateString() : '';
}

export function getPriceWithCurrency(currencyCode = 'USD', totalPrice: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currencyCode }).format(totalPrice);
}

export function addAnalyticsCheckoutFlowMetadata(details: any, captureSearchAnalytics = false) {
  let analyticsDetails: any = {
    ...details,
  };
  const cart = getCart(store.getState() as StoreState);

  analyticsDetails.flow = 'mcgraw';

  if (isTunnelPurchaseFlow() || isConsideredAsTunnel(getCart(store.getState() as StoreState)?.products)) {
    const product = cart?.products[0];
    analyticsDetails.flow = 'tunnel-purchase';
    if (product?.productMetadata) {
      analyticsDetails.application = getOrderApplicationForAnalytics(product.productMetadata);
    }
    analyticsDetails = {
      ...analyticsDetails,
      ...getSharpenTunnelFlowProperties({
        isSharpenCompanion: product?.productIdentifier.isSharpenCompanion,
        isSharpenAdvantage: product?.productIdentifier.isSharpenAdvantage,
        isConnectSharpenBundle: product?.productIdentifier.isConnectSharpenBundle,
        itemType: product?.productMetadata.itemType,
        offerType: product?.productMetadata.offerType,
        formatCode: product?.productMetadata.formatCode,
        flow: product?.productMetadata.flow,
      }),
    };
  }
  details.isEdoms = !!cart?.metaOfferId;
  if (captureSearchAnalytics) {
    analyticsDetails.searchText = getCartPageLocalStorageObject() ?? {};
  }

  return {
    ...analyticsDetails,
    ...getCartAnalyticsOverrides(store.getState() as StoreState)
  };
}

export function isConsideredAsTunnel(products) {
  return isAleksLL(products);
}

function isAleksLL(products) {
  return products && ItemType.aleksll === products[0]?.productMetadata.application;
}

export function getOrderApplicationForAnalytics(details: ProductDetails | PaymentOrderItemsProductMetaData) {
  if (details.application && details.application === STANDALONE_APPLICATION) {
    if (details.itemType) {
      return details.itemType.toLowerCase();
    }

    return details.offerType?.toLowerCase();
  }

  return details.application;
}

export function isMagentoCartInvalid(cart) {
  return (
    cart?.extension_attributes?.availability == CART_INVALID || cart?.items?.find((item) => isProductUnavailable(item))
  );
}

function isProductUnavailable(product: any) {
  if (isProductOutOfStock(product)) {
    return false;
  }
  return product.extension_attributes?.availability != PRODUCT_AVAILABLE;
}

/**
 * Verifies if the cart contains at least one free trial product
 *
 * @param products
 */
export function hasAtLeastOneFreeTrialProduct(products: Product[] | undefined): boolean {
  return !!products && products?.some((product) => product.freeTrialInfo?.isFreeTrial);
}

export function hasOnlyFreeTrialProducts(products: Product[] | undefined) {
  return products && products?.every((product) => product.freeTrialInfo?.isFreeTrial);
}

export function getGuestTypeFromOrderItem(product?: Product) {
  return product?.productMetadata?.application || '';
}

/**
 * Check if the cart contains HE Product
 *
 * @param products
 */
export function hasHeProduct(products: Product[]) {
  return products && products?.some((product) => product.productIdentifier.businessUnit === BusinessUnit.HE);
}

/**
 * Check if the cart contains MHP Product
 *
 * @param products
 */
export function hasMhpProduct(products: Product[]) {
  return products && products?.some((product) => product.productIdentifier.businessUnit === BusinessUnit.PROFESSIONAL);
}

/**
 * Return a boolean indicating if there's bundle discount
 * @param shopCart 
 * @returns boolean
 */
export function hasBundleDiscount(shopCart: ShopCart | undefined): boolean {
  if (!shopCart) {
    return false;
  }
  
  return !!shopCart.bundleDiscount;
}

/**
 * Return a boolean indicating if there's a promoCode applied
 * @param shopCart 
 * @returns boolean
 */
export function hasPromoCodeDiscount(shopCart: ShopCart | undefined): boolean {
  if (!shopCart) {
    return false;
  }
  
  return !!shopCart.discount;
}
